import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Sparkles,
  ChevronRight,
  Star,
  Clock,
  Shield,
} from "lucide-react";
import { useActiveServices, useCreateConversation } from "@/hooks/useServices";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import { PromotionalBanner } from "@/components/PromotionalBanner";
import { ServiceCard } from "@/components/ServiceCard";
import { WelcomeModal } from "@/components/WelcomeModal";
import { supabase } from "@/integrations/supabase/client";

export default function ServicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: services, isLoading, error } = useActiveServices();
  const createConversation = useCreateConversation();
  const [creatingServiceId, setCreatingServiceId] = useState<string | null>(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userFirstName, setUserFirstName] = useState<string | null>(null);

  // Check for welcome modal on mount
  useEffect(() => {
    const needsWelcome = localStorage.getItem('needsWelcome');
    if (needsWelcome === 'true' && user) {
      // Fetch user's first name for personalized greeting
      supabase
        .from('profiles')
        .select('first_name')
        .eq('id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          setUserFirstName(data?.first_name || null);
          setShowWelcome(true);
          localStorage.removeItem('needsWelcome');
        });
    }
  }, [user]);

  const handleServiceClick = async (serviceId: string) => {
    if (!user) {
      navigate("/auth");
      return;
    }

    setCreatingServiceId(serviceId);
    try {
      const result = await createConversation.mutateAsync({
        userId: user.id,
        serviceId,
      });
      if (result?.id) {
        navigate(`/chat/${result.id}`);
      } else {
        toast.error("Erreur lors de la création");
      }
    } catch {
      toast.error("Erreur lors de la création");
    }
    setCreatingServiceId(null);
  };

  return (
    <>
    <WelcomeModal 
      open={showWelcome} 
      onClose={() => setShowWelcome(false)}
      firstName={userFirstName}
    />
    <div className="min-h-screen flex flex-col pb-24 bg-background">
      {/* Hero Header */}
      <header
        className="relative overflow-hidden px-4 sm:px-6 pt-6 sm:pt-8 pb-4 sm:pb-6"
        style={{ paddingTop: "max(2.5rem, calc(env(safe-area-inset-top, 0px) + 1.5rem))" }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent" />
        <div className="relative max-w-2xl mx-auto">
          <div className="flex items-center gap-2 mb-2 sm:mb-3">
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Star size={14} className="sm:hidden text-primary" />
              <Star size={16} className="hidden sm:block text-primary" />
            </div>
            <span className="text-[10px] sm:text-xs font-medium text-primary uppercase tracking-wider">
              Services Premium
            </span>
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mb-1 sm:mb-2">
            Votre Conciergerie
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">Un service sur-mesure, disponible 24h/24</p>
        </div>
      </header>

      <main className="flex-1 px-4 sm:px-6 space-y-4 sm:space-y-6">
        <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6">
          {/* Promotional Banner */}
          <PromotionalBanner />

          {/* Trust Indicators */}
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <div className="text-center p-2 sm:p-3 rounded-xl bg-card border border-border">
              <Clock size={16} className="sm:hidden mx-auto text-primary mb-1" />
              <Clock size={20} className="hidden sm:block mx-auto text-primary mb-1" />
              <p className="text-[10px] sm:text-xs text-muted-foreground">Réponse</p>
              <p className="text-xs sm:text-sm font-medium text-foreground">&lt; 5 min</p>
            </div>
            <div className="text-center p-2 sm:p-3 rounded-xl bg-card border border-border">
              <Star size={16} className="sm:hidden mx-auto text-primary mb-1" />
              <Star size={20} className="hidden sm:block mx-auto text-primary mb-1" />
              <p className="text-[10px] sm:text-xs text-muted-foreground">Satisfaction</p>
              <p className="text-xs sm:text-sm font-medium text-foreground">98%</p>
            </div>
            <div className="text-center p-2 sm:p-3 rounded-xl bg-card border border-border">
              <Shield size={16} className="sm:hidden mx-auto text-primary mb-1" />
              <Shield size={20} className="hidden sm:block mx-auto text-primary mb-1" />
              <p className="text-[10px] sm:text-xs text-muted-foreground">Service</p>
              <p className="text-xs sm:text-sm font-medium text-foreground">VIP</p>
            </div>
          </div>

          {/* CTA Banner */}
          <button
            onClick={() => navigate("/chat/new")}
            className="w-full relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-primary to-primary/80 p-4 sm:p-6 text-left touch-feedback"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-1 sm:mb-2">
                <Sparkles size={14} className="sm:hidden text-primary-foreground" />
                <Sparkles size={18} className="hidden sm:block text-primary-foreground" />
                <span className="text-[10px] sm:text-xs font-medium text-primary-foreground/80 uppercase tracking-wider">
                  Nouvelle demande
                </span>
              </div>
              <h2 className="font-serif text-lg sm:text-xl font-medium text-primary-foreground mb-0.5 sm:mb-1">
                Démarrez une conversation
              </h2>
              <p className="text-xs sm:text-sm text-primary-foreground/70">
                Notre équipe vous répond en quelques minutes
              </p>
            </div>
            <div className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2">
              <ChevronRight size={20} className="sm:hidden text-primary-foreground/50" />
              <ChevronRight size={24} className="hidden sm:block text-primary-foreground/50" />
            </div>
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-white/10 rounded-full blur-2xl" />
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-white/5 rounded-full blur-xl" />
          </button>

          {/* Services Section */}
          <div>
            <h3 className="font-serif text-base sm:text-lg font-medium text-foreground mb-3 sm:mb-4">Nos Services</h3>

            {isLoading ? (
              <div className="space-y-3">
                {[...Array(5)].map((_, i) => (
                  <Skeleton key={i} className="h-24 rounded-xl" />
                ))}
              </div>
            ) : error ? (
              <div className="text-center py-8 bg-card rounded-xl border border-border">
                <p className="text-muted-foreground">Erreur de chargement</p>
                <Button variant="ghost" className="mt-2" onClick={() => window.location.reload()}>
                  Réessayer
                </Button>
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {services?.map((service, index) => (
                  <div
                    key={service.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <ServiceCard
                      name={service.name}
                      description={service.description}
                      bgImageUrl={service.bg_image_url}
                      isLoading={creatingServiceId === service.id}
                      disabled={creatingServiceId === service.id}
                      onClick={() => handleServiceClick(service.id)}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Help Section */}
          <div className="bg-card rounded-xl border border-border p-4 sm:p-5">
            <div className="flex items-start gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <span className="text-primary font-serif text-lg sm:text-xl font-semibold">?</span>
              </div>
              <div className="flex-1">
                <h4 className="font-serif font-medium text-foreground text-sm sm:text-base mb-0.5 sm:mb-1">
                  Besoin d'autre chose ?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                  Notre équipe est à votre disposition pour toute demande spécifique.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate("/chat/new")}
                  className="gap-2 text-xs sm:text-sm"
                >
                  <Sparkles size={14} />
                  Contactez-nous
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <BottomNav />
    </div>
    </>
  );
}
